import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Spin } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import {
  TbHexagonLetterP,
  TbHexagonLetterR,
  TbHexagonLetterS,
  TbSquareLetterC,
  TbSquareLetterG,
} from "react-icons/tb";
import { MdOutlineCancel } from "react-icons/md";
import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import { selectFilterScoredAndSortedPractices } from "../../Redux/practice/practiceSelectors";
import "./style.css";
import {
  crepColorMap,
  crepColorPool,
} from "../FieldReport/utils/helperFunctions";
import { useNavigate } from "react-router-dom";

const PrintView = () => {
  const {
    selectedState,
    selectedCounty,
    landCoverSelections,
    prioritySelections,
  } = useSelector((state) => state.filter);

  const practiceData = useSelector(selectFilterScoredAndSortedPractices);
  const practicesLoading = useSelector((state) => state.practice.loading);
  const [fallbackTriggered, setFallbackTriggered] = useState(false);
  const navigate = useNavigate();
  // if (!practiceData || practiceData.length === 0) {
  //   navigate("/");
  //   return null;
  // }

  useEffect(() => {
    if (!practicesLoading && (!practiceData || practiceData.length === 0)) {
      const timeout = setTimeout(() => {
        setFallbackTriggered(true);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [practicesLoading, practiceData]);

  useEffect(() => {
    if (
      fallbackTriggered &&
      (!selectedState || !selectedCounty || !practiceData?.length)
    ) {
      navigate("/");
    }
  }, [
    fallbackTriggered,
    selectedState,
    selectedCounty,
    practiceData,
    navigate,
  ]);

  const loading = practicesLoading || !practiceData?.length;

  crepColorMap.clear(); // reset it for this print session
  let colorIndex = 0;

  practiceData?.forEach((practice) => {
    practice.crepInfo?.forEach((item) => {
      if (!crepColorMap.has(item.crepname)) {
        crepColorMap.set(
          item.crepname,
          crepColorPool[colorIndex % crepColorPool.length]
        );
        colorIndex++;
      }
    });
  });

  const titleCase = (str) =>
    str
      ?.toLowerCase()
      .split(" ")
      .map((word) => word.replace(word[0], word[0].toUpperCase()))
      .join(" ");

  const date = new Date();
  const currentDate = `${("0" + (date.getMonth() + 1)).slice(-2)}/${(
    "0" + date.getDate()
  ).slice(-2)}/${date.getFullYear() - 2000}`;

  const getLabels = {
    winderosion: "Wind Erosion",
    watererosion: "Water Erosion",
    soilhealth: "Soil Health",
    excesswind: "Excess Wind",
    drought: "Drought",
    flooding: "Flooding",
    snowdrift: "Snow Drift",
    waterquality: "Water Quality",
    aquatic: "Aquatic",
    biggame: "Big Game",
    grasslandbirds: "Grassland Birds",
    forestbirds: "Forest Birds",
    pollinators: "Pollinators",
    waterfowl: "Waterfowl",
    carbonstorage: "Carbon Storage",
    priorityecosystem: "Ecosystem",
    aquaculture: "Aquaculture Ponds",
    cropland: "Cropland",
    timber: "Timber",
    pasture: "Pasture",
    wetland: "Wetland",
  };

  return (
    <>
      <Header noNavigation="print" />
      <div className="print-filter-results">
        <h3 style={{ float: "right", marginRight: "30px" }}>
          {`Location: ${selectedCounty}, ${selectedState}`}
          <span style={{ float: "right" }}>Date: {currentDate}</span>
        </h3>
        <h3>Available Practices: {practiceData?.length}</h3>

        {landCoverSelections?.length > 0 && (
          <h3>
            Selected Land Cover Types:{" "}
            {landCoverSelections.map((type, i) => (
              <b key={i} className="filter-tag">
                {getLabels[type]}
              </b>
            ))}
          </h3>
        )}

        {prioritySelections?.length > 0 && (
          <h3>
            Selected Priorities:{" "}
            {prioritySelections.map((type, i) => (
              <b key={i} className="filter-tag">
                {getLabels[type]}
              </b>
            ))}
          </h3>
        )}

        <Button
          onClick={() => window.print()}
          className="print-button filter-button"
        >
          <b>
            <PrinterOutlined /> Print
          </b>
        </Button>

        <Button onClick={() => window.close()} className="close-button">
          <b className="close-text">
            <MdOutlineCancel /> <span>Close</span>
          </b>
        </Button>
      </div>

      <Spin spinning={loading} size="large">
        <div className="print-container">
          {practiceData?.map((practice, i) => {
            const topRibbons = [];

            if (Array.isArray(practice.crepInfo)) {
              practice.crepInfo.forEach((item) => {
                topRibbons.push({
                  label: item.crepname,
                  color: crepColorMap.get(item.crepname) || "#888",
                });
              });
            }

            if (practice.isMostCommon) {
              topRibbons.unshift({
                label: "Most Common",
                color: "#307d00",
              });
            }

            return (
              <div className="for-print-margin" key={i}>
                <div className="print-practice-container">
                  <div className="print-practice-ribbons">
                    {topRibbons.map((ribbon, idx) => (
                      <div
                        key={idx}
                        className="print-practice-flag"
                        style={{
                          backgroundColor: ribbon.color,
                          marginBottom: "4px",
                          padding: "4px 8px",
                          color: "#fff",
                          display: "inline-block",
                          fontWeight: "bold",
                          borderRadius: "4px",
                          fontSize: "12px",
                          marginRight: "8px",
                        }}
                      >
                        {ribbon.label}
                      </div>
                    ))}
                  </div>

                  <h2 className="print-practice-title">
                    {`${practice.practiceNumber}: ${titleCase(
                      practice.practiceName
                    )}`}
                  </h2>

                  <div className="print-practice-match">
                    {practice.scoreLabel && (
                      <p className={practice.scoreClassName}>
                        {practice.scoreLabel}
                      </p>
                    )}
                  </div>

                  <div>
                    <b>Land Cover:</b>
                    {Array.isArray(practice.landcover) &&
                    practice.landcover.length > 0 ? (
                      <ul className="print-ul">
                        {practice.landcover.map((cover, j) => (
                          <li key={j}>{getLabels[cover]}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>No land cover data available.</p>
                    )}
                  </div>

                  <div>
                    <b>Priorities:</b>
                    {Array.isArray(practice.priorities) &&
                    practice.priorities.length > 0 ? (
                      <ul className="print-ul">
                        {practice.priorities.map(({ name }, j) => (
                          <li key={j}>{getLabels[name]}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>No priority data available.</p>
                    )}
                  </div>

                  <div>
                    <b>Incentives:</b>
                    <div className="print-incentive-container">
                      {!practice.programs?.some(
                        (p) =>
                          p.incentives?.sip ||
                          p.incentives?.pip ||
                          p.incentives?.rentalRateIncentive
                      ) && <p>No additional incentives</p>}

                      {practice.programs?.some(
                        (p) =>
                          p.incentives?.rentalRateIncentive &&
                          p.incentives.rentalRateIncentive !== "false"
                      ) && (
                        <b className="print-incentive-percent">
                          <TbHexagonLetterR
                            size="2em"
                            color="maroon"
                            style={{ marginRight: "3px" }}
                          />
                          Rental Rate Incentive
                        </b>
                      )}

                      {practice.programs?.some(
                        (p) => p.incentives?.pip && p.incentives.pip !== "false"
                      ) && (
                        <b className="print-incentive-percent">
                          <TbHexagonLetterP
                            size="2em"
                            color="maroon"
                            style={{ marginRight: "3px" }}
                          />
                          Practice Incentive
                        </b>
                      )}

                      {practice.programs?.some(
                        (p) => p.incentives?.sip && p.incentives.sip !== "false"
                      ) && (
                        <b className="print-incentive-percent">
                          <TbHexagonLetterS
                            size="2em"
                            color="maroon"
                            style={{ marginRight: "3px" }}
                          />
                          Signup Incentive
                        </b>
                      )}
                    </div>
                  </div>

                  <div>
                    <b>Signups:</b>
                    <div className="print-signup-container">
                      {practice.general && (
                        <b className="print-incentive-percent print-signup">
                          <TbSquareLetterG
                            size="2em"
                            color="maroon"
                            style={{ marginRight: "3px" }}
                          />
                          Available In General Signup
                        </b>
                      )}
                      {practice.continuous === "true" && (
                        <b className="print-incentive-percent print-signup">
                          <TbSquareLetterC
                            size="2em"
                            color="maroon"
                            style={{ marginRight: "3px" }}
                          />
                          Available In Continuous Signup
                        </b>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <p className="disclaimer">
            All information in the Service is provided "as is", with no
            guarantee of completeness, accuracy, timeliness or of the results
            obtained from the use of this information, and without warranty of
            any kind, express or implied, including, but not limited to
            warranties of performance, merchantability and fitness for a
            particular purpose. The University will not be liable to You or
            anyone else for any decision made or action taken in reliance on the
            information given by the Service or for any consequential, special
            or similar damages, even if advised of the possibility of such
            damages. USDA is an equal opportunity provider, employer, and lender
            (https://www.usda.gov/non-discrimination-statement). This work is
            produced with support from the Mississippi State University Forest
            and Wildlife Research Center and Mississippi State University
            Geosystems Research Institute.
          </p>
        </div>
      </Spin>

      <Footer />
    </>
  );
};

export default PrintView;
